import React, { Fragment, useEffect, useState } from "react";
import { Col, Card, CardHeader, CardBody, Form, Label, Row } from 'reactstrap';
import Select from "react-select";
import { Country } from "country-state-city";
import { H3, Breadcrumbs } from "../../../AbstractElements";
import { useForm, Controller } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import Transparentspinner from "../../Common/loader/Transparentspinner";
import { getZoneById, editZone } from '../../../CommonURL';
import { postDataWithToken } from '../../../Utils/restUtils';
import { toast } from 'react-toastify';

const ZoneEdit = () => {
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const zoneId = params.get('id');

    const schema = Yup
        .object()
        .shape({
            name: Yup.string().required('Name is required'),
            countries: Yup.array().min(1, 'Please select at least one country').required(),
        })
        .required();

    const { register, handleSubmit, control, setValue, formState: { errors } } = useForm({
        resolver: yupResolver(schema),
    });

    const [loading, setLoading] = useState(false);
    const [selectedCountries, setSelectedCountries] = useState([]);
    const navigate = useNavigate();

    useEffect(() => {
        fetchZoneDetails();
    }, []);

    const fetchZoneDetails = async () => {
        setLoading(true);
        try {
            const response = await postDataWithToken(getZoneById, { zone_id: zoneId });
            if (response && response.data.status === true) {
                const zone = response.data.data;
                setValue('name', zone.name);
                // Pre-select countries by matching iso codes from the "country" field
                const allCountries = Country.getAllCountries();
                const preSelected = allCountries.filter(c => zone.country.includes(c.isoCode));
                setSelectedCountries(preSelected);
                setValue('countries', preSelected);
            }
        } catch (error) {
            console.error('Error fetching zone details:', error);
        }
        setLoading(false);
    };

    const onSubmit = async (data) => {
        setLoading(true);
        const postRequest = {
            zone_id: zoneId,
            name: data.name,
            countries: data.countries.map(c => c.isoCode),
        };
        const editZoneResponse = await postDataWithToken(editZone, postRequest);
        if (editZoneResponse && editZoneResponse.data && editZoneResponse.data.status === true) {
            toast.success(editZoneResponse.data.message);
            navigate(`${process.env.PUBLIC_URL}/configuration/zone-list`);
        } else {
            toast.error(editZoneResponse?.data?.message || 'Something went wrong');
        }
        setLoading(false);
    };

    return (
        <Fragment>
            {loading && <Transparentspinner />}
            <Breadcrumbs mainTitle="Edit Zone" parent="Configuration" title="Zone" />
            <Card>
                <CardHeader className="pb-0">
                    <H3>Edit Zone</H3>
                </CardHeader>
                <CardBody>
                    <Form className="needs-validation" noValidate onSubmit={handleSubmit(onSubmit)}>
                        <Row className="g-3">
                            <Col md='6'>
                                <Label className="form-label">Name <span className="requireStar">*</span></Label>
                                <input className="form-control" type="text" placeholder="Zone Name" {...register('name')} />
                                <span className="text-danger">{errors.name ? errors.name.message : ''}</span>
                            </Col>
                            <Col md='6'>
                                <Label className="form-label">Countries <span className="requireStar">*</span></Label>
                                <Controller
                                    name="countries"
                                    control={control}
                                    render={({ field }) => (
                                        <Select
                                            {...field}
                                            isMulti
                                            options={Country.getAllCountries()}
                                            getOptionLabel={(opt) => opt.name}
                                            getOptionValue={(opt) => opt.isoCode}
                                            value={selectedCountries}
                                            onChange={(items) => {
                                                setSelectedCountries(items);
                                                field.onChange(items);
                                            }}
                                        />
                                    )}
                                />
                                <span className="text-danger">{errors.countries ? errors.countries.message : ''}</span>
                            </Col>
                        </Row>
                        <br />
                        <button className="btn btn-primary" type="submit">Update</button>
                        <button className="btn btn-secondary ms-2" type="button" onClick={() => navigate(`${process.env.PUBLIC_URL}/configuration/zone-list`)}>Cancel</button>
                    </Form>
                </CardBody>
            </Card>
        </Fragment>
    );
};

export default ZoneEdit;
